import { useEffect, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";

let sdkPromise = null;
function loadPayPalSdk(clientId) {
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=GBP&intent=capture`;
    s.onload = () => resolve(window.paypal);
    s.onerror = reject;
    document.body.appendChild(s);
  });
  return sdkPromise;
}

// Renders PayPal buttons if configured. getPayload() must return {lookup_key, add_script, customer_email, customer_name} or null if invalid.
export default function PayPalButton({ getPayload, onSuccess }) {
  const [enabled, setEnabled] = useState(false);
  const ref = useRef();
  const cfgRef = useRef(null);

  useEffect(() => {
    api.get("/paypal/config").then((r) => { cfgRef.current = r.data; setEnabled(r.data.enabled); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!enabled || !cfgRef.current?.client_id || !ref.current) return;
    let mounted = true;
    loadPayPalSdk(cfgRef.current.client_id).then((paypal) => {
      if (!mounted || !paypal || !ref.current) return;
      ref.current.innerHTML = "";
      paypal.Buttons({
        style: { color: "gold", shape: "pill", label: "paypal", height: 45 },
        createOrder: async () => {
          const payload = getPayload();
          if (!payload) { toast.error("Please enter your name and email first."); throw new Error("invalid"); }
          const { data } = await api.post("/paypal/create-order", { ...payload, origin_url: window.location.origin });
          return data.paypal_order_id;
        },
        onApprove: async (data) => {
          try {
            const { data: res } = await api.post("/paypal/capture-order", { paypal_order_id: data.orderID });
            onSuccess(res);
          } catch (err) { toast.error(apiErr(err.response?.data?.detail) || "Payment could not be completed."); }
        },
        onError: () => toast.error("PayPal error. Please try again or use card."),
      }).render(ref.current);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [enabled, getPayload, onSuccess]);

  if (!enabled) return null;
  return (
    <div>
      <div className="relative my-3 text-center">
        <span className="relative z-10 bg-[#111] px-3 text-xs uppercase tracking-widest text-white/40">or</span>
        <span className="absolute inset-x-0 top-1/2 h-px bg-white/10" />
      </div>
      <div ref={ref} data-testid="paypal-button-container" />
    </div>
  );
}
